/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV6RealGiftLifecycle) return;

  const CART_TIMEOUT_MS = 6000;
  let reconciliation = null;
  let settleTimer = 0;
  let recentlyVerifiedUntil = 0;
  let publishedState = '';
  let repairFingerprint = '';
  let repairObservedAt = 0;
  const scheduleReconcile = (delay = 400) => {
    if (Date.now() < recentlyVerifiedUntil) return;
    if (settleTimer) return;
    settleTimer = window.setTimeout(() => {
      settleTimer = 0;
      reconcileExistingCart();
    }, delay);
  };
  const publishState = (state) => {
    if (publishedState === state && document.documentElement.dataset.safewalkGiftReconcile === state) return;
    publishedState = state;
    document.documentElement.dataset.safewalkGiftReconcile = state;
    document.dispatchEvent(new CustomEvent('safewalk:real-gifts-state', {
      detail: { state }
    }));
  };
  const purchasePending = () => Boolean(document.querySelector('[data-swv5-submit][aria-busy="true"]'));
  const fetchJson = async (input, init = {}, timeoutMs = CART_TIMEOUT_MS) => {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    let timeout;
    const request = (async () => {
      const requestInit = { ...init };
      if (controller && !requestInit.signal) requestInit.signal = controller.signal;
      const response = await window.fetch(input, requestInit);
      return { response, body: await response.json() };
    })();
    const deadline = new Promise((_, reject) => {
      timeout = window.setTimeout(() => {
        reject(new Error('cart-timeout'));
        controller?.abort();
      }, timeoutMs);
    });
    try {
      return await Promise.race([request, deadline]);
    } finally {
      window.clearTimeout(timeout);
    }
  };
  const fetchCart = async (timeoutMs = CART_TIMEOUT_MS) => {
    const { response, body } = await fetchJson('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }, timeoutMs);
    if (!response.ok) throw new Error(`cart-${response.status}`);
    return body;
  };
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const lineTotal = (item) => Number.isFinite(Number(item?.final_line_price))
    ? Number(item.final_line_price)
    : Number(item?.final_price || 0) * Number(item?.quantity || 1);
  const identityRole = (item, config) => (config.identities || []).find((identity) => (
    Number(item?.product_id || 0) === Number(identity.productId)
      && Number(item?.variant_id || item?.id || 0) === Number(identity.variantId)
  ))?.role || '';
  const currentGiftIds = (config) => new Set(
    Object.values(config.realGiftIds || {}).flatMap((ids) => Object.values(ids || {}))
  );
  const readdUrl = (group, config) => {
    if (!group?.main || !config.readdUrl) return '';
    const url = new URL(config.readdUrl, window.location.origin);
    const variantId = Number(group.main.variant_id || group.main.id || 0);
    if (variantId > 0) url.searchParams.set('variant', String(variantId));
    url.searchParams.set('safewalk_readd', '1');
    return url.href;
  };
  const sectionIds = () => {
    const ids = new Set(['cart-drawer', 'cart-icon-bubble']);
    document.querySelectorAll(
      '[data-safewalk-real-gifts-section][data-section-id], cart-items[data-id], #main-cart-footer[data-id]'
    ).forEach((node) => ids.add(node.dataset.sectionId || node.dataset.id));
    document.querySelector('cart-drawer')?.getSectionsToRender?.()
      .forEach((section) => ids.add(section.id));
    return [...ids].filter(Boolean).slice(0, 5);
  };
  const repairCurrentGiftGroup = async (cart) => {
    if (document.querySelector('[data-swv5-submit][aria-busy="true"]')) {
      scheduleReconcile();
      return cart;
    }
    const contract = window.SafeWalkV7KachingNativeContract;
    const config = contract?.configFromRegistry?.() || {};
    const classification = contract?.classifyCart?.(cart, config) || {};
    const groups = classification.groups || [];
    if (groups.length !== 1 || groups[0].complete) {
      repairFingerprint = '';
      repairObservedAt = 0;
      return cart;
    }
    if ((classification.legacyGroups || []).length
      || (classification.legacyOrphanGroups || []).length
      || (classification.legacyInvalidGroups || []).length
      || (classification.legacyInvalidItems || []).length) return cart;
    const group = groups[0];
    const mainMetadata = contract?.metadataFor?.(group.main);
    const expected = config.realGiftIds?.[group.kind] || {};
    if (!group.main || !group.kind || !mainMetadata?.id || !mainMetadata?.v) return cart;
    const fingerprint = [group.groupId, group.kind, ...(group.items || []).map((item) => item.key)]
      .join('|');
    if (repairFingerprint !== fingerprint) {
      repairFingerprint = fingerprint;
      repairObservedAt = Date.now();
      scheduleReconcile(800);
      return cart;
    }
    const unsettledFor = Date.now() - repairObservedAt;
    if (unsettledFor < 700) {
      scheduleReconcile(800 - unsettledFor);
      return cart;
    }
    repairFingerprint = '';
    repairObservedAt = 0;
    const knownGiftIds = currentGiftIds(config);
    const roleLines = new Map();
    for (const item of (group.items || []).filter((candidate) => candidate !== group.main)) {
      const role = identityRole(item, config);
      const metadata = contract?.metadataFor?.(item);
      if (!role || roleLines.has(role)
        || metadata?.deal !== config.deal
        || String(metadata?.id || '') !== String(group.groupId)
        || !knownGiftIds.has(metadata?.gift)
        || Number(item.quantity) !== 1) return cart;
      roleLines.set(role, item);
    }
    let needsRepair = roleLines.size !== (config.identities || []).length;
    for (const identity of config.identities || []) {
      const role = identity.role;
      const existing = roleLines.get(role);
      const metadata = contract?.metadataFor?.(existing);
      const exact = Boolean(existing)
        && metadata?.gift === expected[role]
        && lineTotal(existing) === 0;
      if (!exact) needsRepair = true;
    }
    if (!needsRepair) return cart;
    const removalKeys = (group.items || []).map((item) => item.key).filter(Boolean);
    if (removalKeys.length !== (group.items || []).length) return cart;
    const mainProperties = {
      ...propertiesFor(group.main),
      [contract.KACHING]: JSON.stringify(mainMetadata)
    };
    const mainVariantId = Number(group.main.variant_id || group.main.id || 0);
    if (!mainVariantId) return cart;
    const additions = [{
      id: mainVariantId,
      quantity: 1,
      properties: mainProperties
    }];
    for (const identity of config.identities || []) {
      const role = identity.role;
      const existing = roleLines.get(role);
      const preserved = { ...propertiesFor(existing) };
      delete preserved[contract.KACHING];
      additions.push({
        id: Number(identity.variantId),
        quantity: 1,
        parent_id: mainVariantId,
        properties: {
          ...preserved,
          [contract.KACHING]: JSON.stringify({
            id: String(group.groupId),
            deal: config.deal,
            v: mainMetadata.v,
            gift: expected[role]
          })
        }
      });
    }
    const removed = await fetchJson('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: Object.fromEntries(removalKeys.map((key) => [key, 0])),
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    });
    if (!removed.response.ok || !removed.body?.items) {
      throw new Error(`gift-downgrade-remove-${removed.response.status}`);
    }
    if (removed.body.items.some((item) => removalKeys.includes(item.key))) {
      throw new Error('gift-downgrade-remove-verify');
    }
    const { response, body } = await fetchJson('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: additions,
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    });
    if (!response.ok || !body?.items) throw new Error(`gift-downgrade-add-${response.status}`);
    window.SafeWalkV6RealGifts?.render?.(body);
    const verified = await fetchCart();
    const repaired = contract.classifyCart(verified, config).groups.find((candidate) => (
      candidate.groupId === group.groupId && candidate.kind === group.kind
    ));
    if (!repaired?.complete) throw new Error('gift-downgrade-verify');
    return verified;
  };
  const inspectCart = (cart) => {
    window.SafeWalkV7KachingNativeCart = cart;
    const contract = window.SafeWalkV7KachingNativeContract;
    const config = contract?.configFromRegistry?.() || {};
    const classification = contract?.classifyCart?.(cart, config)
      || {
        groups: [], legacyGroups: [], legacyOrphanGroups: [],
        legacyInvalidGroups: [], legacyInvalidItems: [], invalidGiftItems: []
      };
    const legacyGroups = classification.legacyGroups || [];
    window.SafeWalkV6LegacyGiftGroups = legacyGroups;
    if (legacyGroups.length) document.documentElement.dataset.safewalkGiftRecovery = 'readd';
    else delete document.documentElement.dataset.safewalkGiftRecovery;
    window.SafeWalkV6RealGiftMainIndexes = classification.groups.flatMap((group) => {
      const index = (cart?.items || []).indexOf(group.main);
      return group.complete && index >= 0 && Number(group.main?.quantity) === 1
        ? [index + 1]
        : [];
    });
    window.SafeWalkV6RealGiftMainIndexesReady = true;
    const invalid = classification.invalidGiftItems.length > 0
      || classification.groups.some((group) => !group.complete)
      || legacyGroups.length > 0
      || (classification.legacyOrphanGroups || []).length > 0
      || (classification.legacyInvalidGroups || []).length > 0
      || (classification.legacyInvalidItems || []).length > 0;
    publishState(invalid ? 'invalid' : 'ready');
    return !invalid;
  };
  const cleanupExactOrphans = async (cart) => {
    const contract = window.SafeWalkV7KachingNativeContract;
    const config = contract?.configFromRegistry?.() || {};
    const classification = contract?.classifyCart?.(cart, config) || {};
    const candidates = [
      ...(classification.orphanGiftItems || []),
      ...(classification.legacyOrphanGroups || []).flatMap((group) => group.items || [])
    ];
    if (!candidates.length) return cart;
    const keys = [...new Set(candidates.map((item) => item.key).filter(Boolean))];
    if (keys.length !== candidates.length) throw new Error('orphan-cart-contract');
    const { response, body } = await fetchJson('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        updates: Object.fromEntries(keys.map((key) => [key, 0])),
        sections: sectionIds(),
        sections_url: window.location.pathname
      })
    });
    if (!response.ok || !body?.items) throw new Error(`orphan-remove-${response.status}`);
    const removedKeys = new Set(keys);
    if (body.items.some((item) => removedKeys.has(item.key))) throw new Error('orphan-remove-verify');
    window.SafeWalkV6RealGifts?.render?.(body);
    return body;
  };
  const reconcileOnce = async () => {
    if (purchasePending()) {
      scheduleReconcile();
      return true;
    }
    publishState('pending');
    try {
      const repaired = await repairCurrentGiftGroup(await fetchCart());
      return inspectCart(await cleanupExactOrphans(repaired));
    } catch (error) {
      publishState('invalid');
      return false;
    }
  };
  const reconcileExistingCart = () => {
    if (Date.now() < recentlyVerifiedUntil) return Promise.resolve(true);
    if (reconciliation) return reconciliation;
    reconciliation = reconcileOnce().catch(() => {
      publishState('invalid');
      return false;
    }).finally(() => {
      reconciliation = null;
    });
    return reconciliation;
  };
  const acceptVerifiedCart = (cart) => {
    if (settleTimer) window.clearTimeout(settleTimer);
    settleTimer = 0;
    recentlyVerifiedUntil = Date.now() + 750;
    return inspectCart(cart);
  };
  const isRecentlyVerified = () => Date.now() < recentlyVerifiedUntil;
  const recoverLegacyCart = async () => {
    publishState('pending');
    try {
      const cart = await fetchCart();
      const contract = window.SafeWalkV7KachingNativeContract;
      const config = contract?.configFromRegistry?.() || {};
      const classification = contract?.classifyCart?.(cart, config)
        || {
          groups: [], legacyGroups: [], legacyOrphanGroups: [],
          legacyInvalidGroups: [], legacyInvalidItems: [], invalidGiftItems: []
        };
      const legacyGroups = classification.legacyGroups || [];
      if (legacyGroups.length !== 1
        || classification.invalidGiftItems.length
        || classification.groups.some((group) => !group.complete)
        || (classification.legacyOrphanGroups || []).length
        || (classification.legacyInvalidGroups || []).length
        || (classification.legacyInvalidItems || []).length) {
        throw new Error('legacy-cart-ambiguous');
      }
      const target = legacyGroups[0];
      const targetUrl = readdUrl(target, config);
      const updates = Object.fromEntries(
        target.items.filter((item) => item.key).map((item) => [item.key, 0])
      );
      if (!targetUrl || Object.keys(updates).length !== target.items.length) {
        throw new Error('legacy-cart-contract');
      }
      const { response, body } = await fetchJson('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates })
      });
      if (!response.ok || !body?.items) throw new Error(`legacy-remove-${response.status}`);
      const removedKeys = new Set(Object.keys(updates));
      const verified = contract.classifyCart(body, config);
      if ((verified.legacyGroups || []).length
        || (verified.legacyOrphanGroups || []).length
        || (verified.legacyInvalidGroups || []).length
        || (verified.legacyInvalidItems || []).length
        || verified.invalidGiftItems.length
        || verified.groups.some((group) => !group.complete)
        || body.items.some((item) => removedKeys.has(item.key))) {
        throw new Error('legacy-remove-verify');
      }
      inspectCart(body);
      window.SafeWalkV6RealGifts?.render?.(body);
      return { ok: true, readdUrl: targetUrl };
    } catch (error) {
      publishState('invalid');
      return {
        ok: false,
        reason: error instanceof Error ? error.message : 'legacy-error'
      };
    }
  };

  window.SafeWalkV6RealGiftLifecycle = Object.freeze({
    cleanupUpdates: () => ({}),
    inspectCart,
    acceptVerifiedCart,
    isRecentlyVerified,
    reconcileExistingCart,
    recoverLegacyCart
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcileExistingCart, { once: true });
  } else {
    reconcileExistingCart();
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) reconcileExistingCart();
  });
  document.addEventListener('shopify:section:load', reconcileExistingCart);
  document.addEventListener('safewalk:real-gifts-rendered', () => scheduleReconcile(80));
})();
